import { observer } from "mobx-react-lite";
import { Avatar } from "@/components/Avatar";
import { ElementHighlighter } from "@/components/element-higlighter/ElementHiglighterV2";
import { MinimizedAvatar } from "@/components/MinimizedAvatar";
import ProgressRing from "@/components/ProgressRing";
import {
  AdditionalContentCard,
  FixedContentContainer,
  FixedSubtitlesContainer,
  PromptCardContainer,
  SelectPromptCard,
  SubtitlesCard,
  ShortClipCard,
} from "@/components/PromptCard";
import { ShadowDomContainer } from "@/components/ShadowDomContainer";
import { UserInputConfirmation } from "@/components/UserInputConfirmation";
import { VirtualCursor } from "@/components/VirtualCursor";
import { AvatarManager } from "@/lib/tour/tourPlayback/avatarManager";
import { AutoClickStep } from "@/lib/tour/steps/v2/autoClick";
import { AutoTypeStep } from "@/lib/tour/steps/v2/autoType";
import { HighlightStep } from "@/lib/tour/steps/v2/highlight";
import { UserClickStep } from "@/lib/tour/steps/v2/userClick";
import { UserInputStep } from "@/lib/tour/steps/v2/userInput";
import { DragStep } from "@/lib/tour/steps/v2/drag";
import { AUDIO_ONLY_MINIMIZED_AVATAR_IMG, TourPlaybackManager } from "@/lib/tour/tourPlayback";
import { MultipleChoiceModal } from "../steps/TourModalV2";
import Intro from "@/components/intro/Intro";
import { OpenerSettings } from "@common/schemas";
import AvatarSkeleton from "@/components/AvatarSkeleton";
import { store } from "@/store";
import { AvatarTooltip } from "@/components/AvatarTooltip";

export interface TourPlayerProps {
  tourPlaybackManager?: TourPlaybackManager;
  isPartOfChecklist?: boolean;
  onChecklistButtonClicked?: () => void;
}

export const AdditonalContentWrapper = observer(({ tourPlaybackManager }: TourPlayerProps) => {
  if (!tourPlaybackManager) return null;

  return (
    <>
      {tourPlaybackManager.additionalContent && (
        <FixedContentContainer>
          <AdditionalContentCard
            content={tourPlaybackManager.additionalContent}
            onWatermarkClick={() => tourPlaybackManager.onGiveFeedbackClicked("additionalContent")}
          />
        </FixedContentContainer>
      )}
    </>
  );
});

export const ShortClipWrapper = observer(({ tourPlaybackManager }: TourPlayerProps) => {
  if (!tourPlaybackManager) return null;

  return (
    <>
      {tourPlaybackManager.shortClipMediaUrl && (
        <FixedContentContainer>
          <ShortClipCard
            mediaUrl={tourPlaybackManager.shortClipMediaUrl}
            onProceed={() => {
              tourPlaybackManager.handleShortClipProceed();
            }}
          />
        </FixedContentContainer>
      )}
    </>
  );
});

export const SubtitleCardWrapper = observer(({ tourPlaybackManager }: TourPlayerProps) => {
  if (!tourPlaybackManager?.subtitles || !tourPlaybackManager.showSubtitles) return null;

  return (
    <>
      <FixedSubtitlesContainer inactive={!tourPlaybackManager.showSubtitles}>
        <SubtitlesCard lines={tourPlaybackManager.subtitles} />
      </FixedSubtitlesContainer>
    </>
  );
});

const ProgressRingWrapper = observer(
  ({ avatarSize, avatarManager }: { avatarSize: number; avatarManager: AvatarManager }) => {
    const progress = avatarManager.progress;
    return (
      <div className="pyne-avatar__progress-ring">
        <ProgressRing progress={progress} avatarSize={avatarSize} />
      </div>
    );
  }
);

const AvatarCardWrapper = observer(
  ({ tourPlaybackManager, onChecklistButtonClicked, isPartOfChecklist }: TourPlayerProps) => {
    if (!tourPlaybackManager) return null;

    const avatarManager = tourPlaybackManager.avatarManager;
    const previewImage =
      tourPlaybackManager.tourPlayback.previewImageUrl || tourPlaybackManager.tourPlayback.previewGifUrl;

    return (
      <>
        <Avatar
          videoSrc={avatarManager.mediaUrl}
          previewImage={previewImage}
          previewGifUrl={tourPlaybackManager.avatarBackgroundPreviewGifUrl}
          highlightPlayButton={avatarManager.shouldHighlightPlayButton}
          nextVideoSrc={avatarManager.nextSectionMediaUrl || ""}
          initializedAt={avatarManager.initializedAt}
          isMuted={avatarManager.isMuted}
          isCaptionsVisible={avatarManager.isCaptionsVisible}
          onMuteChange={(isMuted) => avatarManager.onMuteChanged(isMuted)}
          onShowCaptionsChange={(shouldShowCaptions) => avatarManager.onShowCaptionsChanged(shouldShowCaptions)}
          onMediaElementRef={(el) => avatarManager.onMediaElementChanged(el)}
          onPlay={() => avatarManager.onMediaPlay()}
          onCanPlay={() => avatarManager.onMediaCanPlay()}
          onCanPlayThrough={() => avatarManager.onMediaCanPlayThrough()}
          onPause={() => avatarManager.onMediaPaused()}
          onMinimizeClicked={() => avatarManager.onMinimizeClicked()}
          onFinish={() => avatarManager.onMediaFinished()}
          onError={(error) => avatarManager.onMediaError(error)}
          progressRenderer={(width) => <ProgressRingWrapper avatarSize={width} avatarManager={avatarManager} />}
          isAudioOnly={tourPlaybackManager.isAudioAvatar}
          canPlay={avatarManager.canPlay}
          canPause={avatarManager.canPause}
          canSkip={avatarManager.canSkip}
          canReplay={avatarManager.canReplay}
          onPlayClicked={() => avatarManager.onPlayClicked()}
          onPauseClicked={() => avatarManager.onPauseClicked()}
          onReplayClicked={() => avatarManager.onReplayClicked()}
          onSkipClicked={() => avatarManager.onSkipClicked()}
          onGiveFeedbackClicked={() => tourPlaybackManager.onGiveFeedbackClicked("widget")}
          isFeedbackButtonVisible={avatarManager.shouldShowFeedbackButton}
          isControlsAlwaysVisible={avatarManager.isControlsAlwaysVisible}
          isProgressRingVisible={avatarManager.isProgressVisible}
          isBuffering={avatarManager.isBuffering}
          onChecklistButtonClicked={onChecklistButtonClicked}
          isChecklistButtonVisible={isPartOfChecklist}
        />
      </>
    );
  }
);

export const TourPlayer = observer(
  ({ tourPlaybackManager, onChecklistButtonClicked, isPartOfChecklist }: TourPlayerProps) => {
    if (!tourPlaybackManager) {
      return null;
    }

    const promptElement = tourPlaybackManager.prompt ? (
      <SelectPromptCard
        title={tourPlaybackManager.prompt.title}
        content={tourPlaybackManager.prompt.text}
        choices={tourPlaybackManager.prompt.choices}
        finishedTourGuideIds={tourPlaybackManager.finishedTourGuideIds}
        onSelect={(id) => {
          tourPlaybackManager.onQuestionChoiceSelected(id);
        }}
        onWatermarkClick={() => tourPlaybackManager.onGiveFeedbackClicked("question")}
      />
    ) : null;

    if (tourPlaybackManager.isWaitingForIntro) {
      return (
        <ShadowDomContainer>
          <Intro
            type={tourPlaybackManager.settings.opener_type}
            data={tourPlaybackManager.settings.opener_settings as OpenerSettings}
            onStartTour={() => tourPlaybackManager.onIntroStartTourClicked()}
            onCloseTour={() => tourPlaybackManager.onIntroCloseTourClicked()}
            onPostponeTour={() => tourPlaybackManager.onIntroPostponeTourClicked()}
          />
        </ShadowDomContainer>
      );
    }

    const isInitializing = tourPlaybackManager.isInitializing;
    const isMinimized = tourPlaybackManager.avatarManager.isMinimized;
    const isWidgetVisible = !isInitializing && !isMinimized;
    const isSkeletonVisible = isInitializing && !isMinimized;
    const isHighlitableStep =
      tourPlaybackManager.currentStep instanceof DragStep ||
      tourPlaybackManager.currentStep instanceof UserClickStep ||
      tourPlaybackManager.currentStep instanceof HighlightStep ||
      tourPlaybackManager.currentStep instanceof AutoTypeStep ||
      tourPlaybackManager.currentStep instanceof AutoClickStep ||
      tourPlaybackManager.currentStep instanceof UserInputStep;
    const showHighlight =
      (tourPlaybackManager.currentStep instanceof DragStep && !tourPlaybackManager.currentStep.isDragging) ||
      isHighlitableStep;

    const dragDirection =
      tourPlaybackManager.currentStep instanceof DragStep
        ? tourPlaybackManager.currentStep.attributes.direction
        : undefined;

    const currentUserInputStep =
      tourPlaybackManager.currentStep instanceof UserInputStep ? tourPlaybackManager.currentStep : undefined;

    return (
      <div>
        <ShadowDomContainer>
          <div
            id="pyne-minimized-widget-wrapper"
            style={{
              position: "relative",
              zIndex: "var(--pyne-layer-avatar)",
              opacity: isMinimized ? 1 : 0,
              transition: "opacity 0.2s ease-out",
            }}
          >
            {isMinimized && (
              <MinimizedAvatar onDismiss={() => tourPlaybackManager.avatarManager.onDismissClicked()}>
                <AvatarTooltip content="Click to continue" tooltipProps={{ placement: "right" }}>
                  <button
                    className="pyne-minimized-avatar__button"
                    onClick={() => tourPlaybackManager.avatarManager.onExpandClicked()}
                  >
                    <img
                      src={
                        tourPlaybackManager.isAudioAvatar
                          ? AUDIO_ONLY_MINIMIZED_AVATAR_IMG
                          : (tourPlaybackManager.tourPlayback.previewImageUrl ??
                            tourPlaybackManager.tourPlayback.previewGifUrl)
                      }
                      style={{
                        marginRight: "8px",
                      }}
                      alt="Avatar preview"
                      className="pyne-minimized-avatar__preview-img"
                      draggable={false}
                    />
                  </button>
                </AvatarTooltip>
              </MinimizedAvatar>
            )}
          </div>
          <div
            id="pyne-widget-wrapper"
            style={{
              position: "relative",
              opacity: isWidgetVisible ? 1 : 0,
              transition: "opacity 0.2s ease-out",
              zIndex: "var(--pyne-layer-avatar)",
              pointerEvents: isWidgetVisible ? "auto" : "none",
            }}
          >
            <style>{store.widgetPosition.stylesheetContent}</style>
            <AvatarCardWrapper
              tourPlaybackManager={tourPlaybackManager}
              onChecklistButtonClicked={onChecklistButtonClicked}
              isPartOfChecklist={isPartOfChecklist}
            />
            <AdditonalContentWrapper tourPlaybackManager={tourPlaybackManager} />
            <ShortClipWrapper tourPlaybackManager={tourPlaybackManager} />
            <PromptCardContainer>{promptElement}</PromptCardContainer>
            <SubtitleCardWrapper tourPlaybackManager={tourPlaybackManager} />
            {currentUserInputStep && tourPlaybackManager.currentTargetElement && (
              <UserInputConfirmation
                target={tourPlaybackManager.currentTargetElement}
                onProceed={() => currentUserInputStep.handleProceed()}
                placement={currentUserInputStep.attributes.confirmationPopup.position}
                proceedButtonText={currentUserInputStep.attributes.confirmationPopup.proceedButtonText || "Proceed"}
              />
            )}
            {tourPlaybackManager.modalContent && (
              <MultipleChoiceModal
                title={tourPlaybackManager.modalContent.title}
                description={tourPlaybackManager.modalContent.description}
                content={tourPlaybackManager.modalContent.modalContent!}
                choices={tourPlaybackManager.modalContent.choices}
                variant={tourPlaybackManager.modalContent.variant}
                onSelect={(id) => {
                  tourPlaybackManager.onModalChoiceSeleceted(id);
                }}
                onClose={() => tourPlaybackManager.dismissTour()}
                onWatermarkClick={() => tourPlaybackManager.onGiveFeedbackClicked("modal")}
              ></MultipleChoiceModal>
            )}
          </div>
          <div
            id="pyne-avatar-skeleton-wrapper"
            style={{
              position: "relative",
              zIndex: "var(--pyne-layer-avatar)",
              opacity: isSkeletonVisible ? 1 : 0,
              transition: "opacity 0.2s ease-in-out",
              pointerEvents: "none",
            }}
          >
            <AvatarSkeleton />
          </div>
        </ShadowDomContainer>
        {showHighlight && tourPlaybackManager.currentTargetElement && (
          <ElementHighlighter
            higlightType={tourPlaybackManager.currentStep.attributes.targetElementHighlightStyle}
            target={tourPlaybackManager.currentTargetElement}
          />
        )}
        <VirtualCursor
          id="pyne-cursor"
          target={tourPlaybackManager.currentTargetElement}
          visible={tourPlaybackManager.cursorSettings.visible}
          click={tourPlaybackManager.cursorSettings.click as any}
          direction={dragDirection}
        />
      </div>
    );
  }
);
